import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMutation } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import { Grid } from '@mui/material'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import AddIcon from '@mui/icons-material/Add'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import { getNotifybarList } from 'app/redux/store/features/notifybars'
import NotifyGridFilter from './components/NotifyGridFilter'
import NotifyGridData from './components/NotifyGridData'
import { Capitalize } from 'app/utils/components/StringUtils/stringUtils'
import { useSnackbar } from 'notistack'

const NotifyBar = () => {
    const { state } = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const [filter, setFilter] = useState({
        heading: '',
        sort: 'Heading',
        isDescending: false,
    })
    const { notifybars, loading, error } = useSelector((state) => state.notifybars)
    const [apiCallInProgress, setApiCallInProgress] = useState(false)
    const [currentPage, setCurrentPage] = useState(0)
    const pageSize = 25

    useEffect(() => {
        if (!apiCallInProgress || loading) return
        setApiCallInProgress(false)

        if (error) handleError(error)
    }, [loading])

    useEffect(() => {
        const shouldFetch = !state?.navigateFrom || !notifybars.data?.length
        if (shouldFetch) {
            setApiCallInProgress(true)
            dispatch(getNotifybarList({ ...filter, skip: 0, take: pageSize }))
        }
    }, [state?.navigateFrom, dispatch])

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    function init(initialValue) {
        return {
            gridFilterVisible: initialValue.gridFilterVisible,
            filterIconColor: initialValue.filterIconColor,
        }
    }

    function notifyReducer(state, action) {
        switch (action.type) {
            case 'set-grid-filter':
                return {
                    ...state,
                    gridFilterVisible: action.payload,
                }
            case 'set-filter-icon-color':
                return {
                    ...state,
                    filterIconColor: action.payload,
                }
            default:
                return state
        }
    }

    const [notifyGrid, setNotifyGrid] = React.useReducer(
        notifyReducer,
        {
            gridFilterVisible: false,
            filterIconColor: false,
        },
        init
    )

    const applyFilter = useMutation({
        mutationFn: (currentfilter) => {
            dispatch(getNotifybarList({ ...currentfilter, skip: 0, take: pageSize }, true))
            setFilter(currentfilter)
        },
    })

    const onPageChange = (pageNumber) => {
        const currentFilter = { ...filter, skip: pageSize * pageNumber, take: pageSize }
        dispatch(getNotifybarList(currentFilter))
        setFilter(currentFilter)
    }

    const onColumnSort = (sortOrder) => {
        const columnName = Capitalize(sortOrder?.[0]?.columnName || '')
        const currentFilter = {
            ...filter,
            sort: columnName,
            isDescending: sortOrder[0].direction !== 'asc',
            take: pageSize,
        }
        dispatch(getNotifybarList(currentFilter))
        setFilter(currentFilter)
    }

    const onRefresh = () => {
        setApiCallInProgress(true)
        dispatch(getNotifybarList({ ...filter, skip: 0, take: pageSize }, true))
        setCurrentPage(0)
    }

    const setGridFilter = React.useCallback(
        (value) => {
            setNotifyGrid({ type: 'set-grid-filter', payload: value })
        },
        [setNotifyGrid]
    )

    const onAddNotify = () => {
        navigate('/settings/configuration/notify-bar/add')
    }

    const setFilterIconColor = React.useCallback(
        (value) => {
            setNotifyGrid({ type: 'set-filter-icon-color', payload: value })
        },
        [setNotifyGrid]
    )

    return (
        <JumboCardQuick
            noWrapper
            title={
                <Typography color={'common.white'} variant={'h3'}>
                    <AutorenewRoundedIcon
                        fontSize={'large'}
                        onClick={onRefresh}
                        sx={{
                            mb: -1.5,
                            mr: 0.5,
                            ml: -2,
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                    />
                    Notify List
                </Typography>
            }
            headerSx={{
                height: 40,
                bgcolor: 'success.main',
            }}
            action={
                <Stack direction={'row'} spacing={4} sx={{ mt: -2 }}>
                    <AddIcon
                        fontSize={'large'}
                        variant={'contained'}
                        sx={{
                            pt: 0,
                            pb: 0,
                            color: 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={onAddNotify}
                    />
                    <FilterAltIcon
                        fontSize='large'
                        sx={{
                            mt: -1.8,
                            color:
                                notifyGrid.filterIconColor && !notifyGrid.gridFilterVisible
                                    ? 'success.main'
                                    : 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={() => setGridFilter(!notifyGrid.gridFilterVisible)}
                    />
                </Stack>
            }>
            <Grid>
                <NotifyGridFilter
                    mutation={applyFilter}
                    display={notifyGrid.gridFilterVisible ? '' : 'none'}
                    filterIconColor={(data) => setFilterIconColor(data)}
                />
            </Grid>
            <Grid>
                <NotifyGridData
                    onRefresh={onRefresh}
                    onPageChange={onPageChange}
                    onColumnSort={onColumnSort}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    handleError={handleError}
                />
            </Grid>
        </JumboCardQuick>
    )
}
export default NotifyBar
